/* eslint-disable formatjs/no-literal-string-in-jsx */
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { JiraIcon } from '@atlaskit/logo';
import ModalDialog from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives/compiled';
import { RadioGroup } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import { Modal } from '@atlassian/growth-pattern-library-modal';
import { css } from '@compiled/react';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { ModalOnboardingImage } from './ModalOnboardingImage';
import { messages } from './messages';
import {
	COPY_LIST_SETTINGS_NO,
	COPY_LIST_SETTINGS_YES,
	MODAL_HEIGHT,
	MODAL_WIDTH,
	TELL_ME_MORE_URL,
} from '../placements/in-app-v2/issue-navigator-placement/constants';

type HighImpactModalProps = {
	onClose: () => void;
	onExploreListViewButtonClick: () => void;
	hasListPreference?: boolean;
	copyListSettings: string;
	setCopyListSettings: (value: string) => void;
};

const linkContainerStyles = css({
	display: 'flex',
	// Make the focus ring sit within the bounds of the modal content
	'a:focus, a:focus-visible': {
		outlineOffset: token('space.negative.025'),
		borderRadius: token('radius.small', '4px'),
	},
});

const ModalContentWithListPreference = ({
	copyListSettings,
	setCopyListSettings,
}: {
	copyListSettings: string;
	setCopyListSettings: (value: string) => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<>
			<Text>{formatMessage(messages.description)}</Text>
			<div css={linkContainerStyles}>
				<Link href={TELL_ME_MORE_URL} target="_blank">
					{formatMessage(messages.learnMoreLink)}
				</Link>
			</div>
			<Text weight="bold">{formatMessage(messages.copyListSettingsDescription)}</Text>
			<RadioGroup
				options={[
					{
						label: formatMessage(messages.copyListSettingsYes),
						value: COPY_LIST_SETTINGS_YES,
					},
					{
						label: formatMessage(messages.copyListSettingsNo),
						value: COPY_LIST_SETTINGS_NO,
					},
				]}
				defaultValue={copyListSettings}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-all-member-expressions
					setCopyListSettings(event.target.value);
				}}
			/>
		</>
	);
};

const ModalContentWithoutListPreference = () => {
	const { formatMessage } = useIntl();
	return (
		<>
			<Text>{formatMessage(messages.description)}</Text>
			<Text>{formatMessage(messages.filtersAndGroupsDescription)}</Text>
			<div css={linkContainerStyles}>
				<Link href={TELL_ME_MORE_URL} target="_blank">
					{formatMessage(messages.learnMoreLink)}
				</Link>
			</div>
		</>
	);
};
export const HighImpactModal = ({
	onClose,
	onExploreListViewButtonClick,
	hasListPreference,
	copyListSettings,
	setCopyListSettings,
}: HighImpactModalProps) => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const exploreButtonRef = useRef<HTMLButtonElement | null>(null);

	return (
		<ModalDialog
			width={MODAL_WIDTH}
			height={MODAL_HEIGHT}
			onOpenComplete={() => {
				fireAnalyticsEvent({
					eventType: 'screen',
					name: 'jiraNinListMergeModalOnboardingViewed',
					attributes: {
						hasListPreference,
					},
				});
			}}
			onCloseComplete={() => {
				fireAnalyticsEvent({
					eventType: 'screen',
					name: 'jiraNinListMergeModalOnboardingClosed',
					attributes: {
						hasListPreference,
					},
				});
			}}
			testId="high-impact-modal"
			autoFocus={exploreButtonRef}
			onClose={onClose}
		>
			<Modal
				renderedInsideEntrypoint
				title={<Heading size="medium">{formatMessage(messages.heading)}</Heading>}
				sideContent={<ModalOnboardingImage onClose={onClose} />}
				actions={
					<Button
						appearance="primary"
						onClick={onExploreListViewButtonClick}
						ref={exploreButtonRef}
					>
						{formatMessage(messages.exploreListViewButtonLabel)}
					</Button>
				}
				logo={<JiraIcon appearance="brand" shouldUseNewLogoDesign />}
			>
				{hasListPreference ? (
					<ModalContentWithListPreference
						copyListSettings={copyListSettings}
						setCopyListSettings={setCopyListSettings}
					/>
				) : (
					<ModalContentWithoutListPreference />
				)}
			</Modal>
		</ModalDialog>
	);
};
